/**
 * Generated by orval
 * Do not edit manually.
 * IP Fabric API
 * IP Fabric API documentation. For details see [IP Fabric API documentation](https://docs.ipfabric.io/main/IP_Fabric_API/)
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  GetApiTokensRoles200Item,
  PostTablesSettingsApiTokens200,
  PostTablesSettingsApiTokensBody,
} from '../../schemas';

import { apiClient } from '../../../apiClient';

/**
 * Returns the list of available API tokens with their parameters
 * @summary Return all API tokens
 */
export const getApiTokens = (signal?: AbortSignal) => {
  return apiClient<unknown>({ url: `/api-tokens`, method: 'GET', signal });
};

export const getGetApiTokensQueryKey = () => {
  return [`/api-tokens`] as const;
};

export const getGetApiTokensQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiTokens>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<Awaited<ReturnType<typeof getApiTokens>>, TError, TData>
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiTokensQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getApiTokens>>> = ({
    signal,
  }) => getApiTokens(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiTokens>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiTokensQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApiTokens>>
>;
export type GetApiTokensQueryError = unknown;

export function useGetApiTokens<
  TData = Awaited<ReturnType<typeof getApiTokens>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getApiTokens>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTokens>>,
          TError,
          Awaited<ReturnType<typeof getApiTokens>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetApiTokens<
  TData = Awaited<ReturnType<typeof getApiTokens>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getApiTokens>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTokens>>,
          TError,
          Awaited<ReturnType<typeof getApiTokens>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetApiTokens<
  TData = Awaited<ReturnType<typeof getApiTokens>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getApiTokens>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Return all API tokens
 */

export function useGetApiTokens<
  TData = Awaited<ReturnType<typeof getApiTokens>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getApiTokens>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetApiTokensQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Creates a new API token to access platform data with selected role and expiration (optional)
 * @summary Create API token
 */
export const postApiTokens = (signal?: AbortSignal) => {
  return apiClient<unknown>({ url: `/api-tokens`, method: 'POST', signal });
};

export const getPostApiTokensMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postApiTokens>>,
    TError,
    void,
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postApiTokens>>,
  TError,
  void,
  TContext
> => {
  const mutationKey = ['postApiTokens'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postApiTokens>>,
    void
  > = () => {
    return postApiTokens();
  };

  return { mutationFn, ...mutationOptions };
};

export type PostApiTokensMutationResult = NonNullable<
  Awaited<ReturnType<typeof postApiTokens>>
>;

export type PostApiTokensMutationError = unknown;

/**
 * @summary Create API token
 */
export const usePostApiTokens = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof postApiTokens>>,
      TError,
      void,
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof postApiTokens>>,
  TError,
  void,
  TContext
> => {
  const mutationOptions = getPostApiTokensMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Deletes the API token from the platform specified by the token ID
 * @summary Delete API token
 */
export const deleteApiTokensKey = (key: string) => {
  return apiClient<unknown>({ url: `/api-tokens/${key}`, method: 'DELETE' });
};

export const getDeleteApiTokensKeyMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteApiTokensKey>>,
    TError,
    { key: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteApiTokensKey>>,
  TError,
  { key: string },
  TContext
> => {
  const mutationKey = ['deleteApiTokensKey'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteApiTokensKey>>,
    { key: string }
  > = (props) => {
    const { key } = props ?? {};

    return deleteApiTokensKey(key);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteApiTokensKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteApiTokensKey>>
>;

export type DeleteApiTokensKeyMutationError = unknown;

/**
 * @summary Delete API token
 */
export const useDeleteApiTokensKey = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof deleteApiTokensKey>>,
      TError,
      { key: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof deleteApiTokensKey>>,
  TError,
  { key: string },
  TContext
> => {
  const mutationOptions = getDeleteApiTokensKeyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Updates the API's token parameters (description, expiration, role)
 * @summary Update API token
 */
export const putApiTokensKey = (key: string) => {
  return apiClient<unknown>({ url: `/api-tokens/${key}`, method: 'PUT' });
};

export const getPutApiTokensKeyMutationOptions = <
  TError = unknown,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putApiTokensKey>>,
    TError,
    { key: string },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putApiTokensKey>>,
  TError,
  { key: string },
  TContext
> => {
  const mutationKey = ['putApiTokensKey'];
  const { mutation: mutationOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putApiTokensKey>>,
    { key: string }
  > = (props) => {
    const { key } = props ?? {};

    return putApiTokensKey(key);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutApiTokensKeyMutationResult = NonNullable<
  Awaited<ReturnType<typeof putApiTokensKey>>
>;

export type PutApiTokensKeyMutationError = unknown;

/**
 * @summary Update API token
 */
export const usePutApiTokensKey = <TError = unknown, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof putApiTokensKey>>,
      TError,
      { key: string },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof putApiTokensKey>>,
  TError,
  { key: string },
  TContext
> => {
  const mutationOptions = getPutApiTokensKeyMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Returns all available roles from user management settings
 * @summary Return all roles
 */
export const getApiTokensRoles = (signal?: AbortSignal) => {
  return apiClient<GetApiTokensRoles200Item[]>({
    url: `/api-tokens/roles`,
    method: 'GET',
    signal,
  });
};

export const getGetApiTokensRolesQueryKey = () => {
  return [`/api-tokens/roles`] as const;
};

export const getGetApiTokensRolesQueryOptions = <
  TData = Awaited<ReturnType<typeof getApiTokensRoles>>,
  TError = unknown,
>(options?: {
  query?: Partial<
    UseQueryOptions<
      Awaited<ReturnType<typeof getApiTokensRoles>>,
      TError,
      TData
    >
  >;
}) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetApiTokensRolesQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getApiTokensRoles>>
  > = ({ signal }) => getApiTokensRoles(signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getApiTokensRoles>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiTokensRolesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getApiTokensRoles>>
>;
export type GetApiTokensRolesQueryError = unknown;

export function useGetApiTokensRoles<
  TData = Awaited<ReturnType<typeof getApiTokensRoles>>,
  TError = unknown,
>(
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getApiTokensRoles>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTokensRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiTokensRoles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetApiTokensRoles<
  TData = Awaited<ReturnType<typeof getApiTokensRoles>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getApiTokensRoles>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getApiTokensRoles>>,
          TError,
          Awaited<ReturnType<typeof getApiTokensRoles>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetApiTokensRoles<
  TData = Awaited<ReturnType<typeof getApiTokensRoles>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getApiTokensRoles>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Return all roles
 */

export function useGetApiTokensRoles<
  TData = Awaited<ReturnType<typeof getApiTokensRoles>>,
  TError = unknown,
>(
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getApiTokensRoles>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetApiTokensRolesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * This table shows API token management for the IP Fabric platform. It displays a list of active API tokens and their associated details, including usernames, descriptions, expiration status, last used timestamps, roles, and usage counts.
 * @summary API Tokens
 */
export const postTablesSettingsApiTokens = (
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  signal?: AbortSignal,
) => {
  return apiClient<PostTablesSettingsApiTokens200>({
    url: `/tables/settings/api-tokens`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: postTablesSettingsApiTokensBody,
    signal,
  });
};

export const getPostTablesSettingsApiTokensQueryKey = (
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
) => {
  return [
    `/tables/settings/api-tokens`,
    postTablesSettingsApiTokensBody,
  ] as const;
};

export const getPostTablesSettingsApiTokensQueryOptions = <
  TData = Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
  TError = unknown,
>(
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
        TError,
        TData
      >
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getPostTablesSettingsApiTokensQueryKey(postTablesSettingsApiTokensBody);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof postTablesSettingsApiTokens>>
  > = ({ signal }) =>
    postTablesSettingsApiTokens(postTablesSettingsApiTokensBody, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type PostTablesSettingsApiTokensQueryResult = NonNullable<
  Awaited<ReturnType<typeof postTablesSettingsApiTokens>>
>;
export type PostTablesSettingsApiTokensQueryError = unknown;

export function usePostTablesSettingsApiTokens<
  TData = Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
  TError = unknown,
>(
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
          TError,
          Awaited<ReturnType<typeof postTablesSettingsApiTokens>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsApiTokens<
  TData = Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
  TError = unknown,
>(
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
          TError,
          Awaited<ReturnType<typeof postTablesSettingsApiTokens>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function usePostTablesSettingsApiTokens<
  TData = Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
  TError = unknown,
>(
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary API Tokens
 */

export function usePostTablesSettingsApiTokens<
  TData = Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
  TError = unknown,
>(
  postTablesSettingsApiTokensBody: PostTablesSettingsApiTokensBody,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof postTablesSettingsApiTokens>>,
        TError,
        TData
      >
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getPostTablesSettingsApiTokensQueryOptions(
    postTablesSettingsApiTokensBody,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}
